import React from 'react';
import { Popover as BasePopover } from '@base-ui-components/react/popover';
import { Tooltip as BaseTooltip } from '@base-ui-components/react/tooltip';
import { useModalContainer } from './Modal';

// Compatibility wrapper for react-bootstrap's OverlayTrigger. The overlay
// prop takes a ui/Popover or ui/Tooltip element; the child is the trigger.
// Click triggers use Base UI Popover; hover/focus triggers use Base UI
// Tooltip (Base UI popovers are click-driven by design).

const SIDE = { top: 'top', bottom: 'bottom', left: 'left', right: 'right' };

/**
 * @param {object} props
 * @param {'hover'|'focus'|'click'|Array<'hover'|'focus'|'click'>} [props.trigger=['hover','focus']]
 *   Including 'click' switches from Tooltip to Popover semantics (stays open
 *   until outside press).
 * @param {'top'|'bottom'|'left'|'right'} [props.placement='right']
 * @param {React.ReactElement} props.overlay A ui Tooltip or Popover element.
 * @param {number} [props.delayShow] Hover-trigger open delay in ms.
 * @param {number} [props.delayHide] Hover-trigger close delay in ms.
 * @param {React.ReactElement} props.children Exactly one trigger element.
 */
const OverlayTrigger = ({ trigger = ['hover', 'focus'], placement = 'right', overlay, rootClose, delayShow, delayHide, children, ...rest }) => {
  const triggers = [].concat(trigger);
  const side = SIDE[placement] || 'right';
  const child = React.Children.only(children);
  // Inside a ui Modal, portal into the dialog's popup instead of <body>,
  // like the ui menus and poppers: keeps overlay content in the dialog's
  // DOM subtree (react-bootstrap rendered overlays inline).
  const modalContainer = useModalContainer();

  if (triggers.includes('click')) {
    return (
      <BasePopover.Root modal={false} {...rest}>
        <BasePopover.Trigger render={child} />
        <BasePopover.Portal container={modalContainer}>
          <BasePopover.Positioner side={side} sideOffset={6}>
            <BasePopover.Popup>{overlay}</BasePopover.Popup>
          </BasePopover.Positioner>
        </BasePopover.Portal>
      </BasePopover.Root>
    );
  }

  return (
    <BaseTooltip.Root {...rest}>
      <BaseTooltip.Trigger render={child} delay={delayShow} closeDelay={delayHide} />
      <BaseTooltip.Portal container={modalContainer}>
        <BaseTooltip.Positioner side={side} sideOffset={6}>
          <BaseTooltip.Popup>{overlay}</BaseTooltip.Popup>
        </BaseTooltip.Positioner>
      </BaseTooltip.Portal>
    </BaseTooltip.Root>
  );
};

export default OverlayTrigger;
